import type * as React from "react";
import { useState } from "react";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { ActivationLinkStatus } from "@nasam/core";
import { Button } from "@nasam/ui";

// The reset-password variant never reports SUPERSEDED; the activation variant
// can (a resent link supersedes the one just checked). This screen's template
// is shared by both, so it handles the union of both statuses.
export type AnyLinkStatus = ActivationLinkStatus;

export interface LinkStatusContent {
  readonly title: string;
  readonly description: string;
}

export function resolveLinkStatusContent(
  status: AnyLinkStatus,
  t: TFunction,
): LinkStatusContent {
  switch (status) {
    case "VALID":
      return {
        title: t("authentication:linkValidTitle"),
        description: t("authentication:linkValidDescription"),
      };
    case "EXPIRED":
      return {
        title: t("authentication:linkExpiredTitle"),
        description: t("authentication:linkExpiredDescription"),
      };
    case "ALREADY_USED":
      return {
        title: t("authentication:linkAlreadyUsedTitle"),
        description: t("authentication:linkAlreadyUsedDescription"),
      };
    case "SUPERSEDED":
      return {
        title: t("authentication:linkSupersededTitle"),
        description: t("authentication:linkSupersededDescription"),
      };
    case "INVALID":
      return {
        title: t("authentication:linkInvalidTitle"),
        description: t("authentication:linkInvalidDescription"),
      };
    default: {
      const _exhaustiveCheck: never = status;
      throw new Error(`Unhandled link status: ${String(_exhaustiveCheck)}`);
    }
  }
}

/**
 * Full-bleed shell shared by the "link expired/invalid/used" outcomes.
 * Matches the standalone Figma frames "Expired forget password"
 * (2676:432100) and "Activation Expired Portal" (5547:83624): a light-gray
 * page with a large rounded white panel holding a centered illustration,
 * a 52px title, an 24px description, and a primary call to action. Those
 * two frames are the only outcomes Figma designed explicitly (both are the
 * EXPIRED case); VALID/ALREADY_USED/INVALID reuse the same template with
 * their existing i18n copy since no distinct design exists for them.
 */
function LinkStatusFullBleed({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  return (
    <main
      dir={i18n.dir()}
      className="animate-rise flex min-h-screen items-center justify-center bg-neutral-100 p-4 lg:p-5"
    >
      <div className="flex w-full max-w-[87.4375rem] flex-col items-center justify-center gap-9 rounded-lg bg-container-default-background px-6 py-16 text-center lg:min-h-[57rem]">
        {children}
      </div>
    </main>
  );
}

export interface LinkStatusViewProps {
  readonly status: AnyLinkStatus;
  readonly mode?: "activation" | "reset" | "generic" | undefined;
  readonly email?: string | null | undefined;
  /** Pre-translated description from the link-status check, preferred over the local i18n copy when present. */
  readonly message?: string | null | undefined;
  readonly onResendActivationLink?: (() => Promise<void> | void) | undefined;
  readonly onBackToLogin?: (() => void) | undefined;
  readonly onContinue?: (() => void) | undefined;
}

export function LinkStatusView({
  status,
  mode = "generic",
  message,
  onResendActivationLink,
  onBackToLogin,
  onContinue,
}: LinkStatusViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const content = resolveLinkStatusContent(status, t);
  const description =
    message !== null && message !== undefined && message.length > 0
      ? message
      : content.description;

  const handleBackToLogin = () => {
    if (onBackToLogin) {
      onBackToLogin();
    } else {
      void navigate({ to: "/auth/login" });
    }
  };

  const handleResend = async () => {
    if (!onResendActivationLink) return;
    setErrorMessage(null);
    try {
      setResending(true);
      await onResendActivationLink();
      setResendSuccess(true);
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : t("common:somethingWentWrong"),
      );
    } finally {
      setResending(false);
    }
  };

  const showResendButton =
    status === "EXPIRED" &&
    mode === "activation" &&
    onResendActivationLink !== undefined;

  return (
    <LinkStatusFullBleed>
      <img
        src="/brand/link-status-illustration.png"
        alt=""
        aria-hidden="true"
        className="w-full max-w-[30rem]"
      />

      <div className="flex flex-col items-center gap-2">
        <h1 className="text-heading-h2-bold text-text">{content.title}</h1>
        <p className="text-heading-h5-semibold text-text-placeholder">
          {description}
        </p>
      </div>

      {errorMessage !== null && (
        <div
          role="alert"
          className="animate-in fade-in-0 slide-in-from-top-1 w-full max-w-[25.5625rem] rounded-md border border-fail-text bg-container-default-background p-3 text-body-s-r text-fail-text"
        >
          {errorMessage}
        </div>
      )}

      {resendSuccess && (
        <div
          role="status"
          className="animate-in fade-in-0 slide-in-from-top-1 w-full max-w-[25.5625rem] rounded-md border border-container-default-border bg-container-default-background p-3 text-body-s-r text-text"
        >
          {t("authentication:activationLinkResentSuccess")}
        </div>
      )}

      <div className="flex w-full max-w-[25.5625rem] flex-col gap-2">
        {status === "VALID" && onContinue !== undefined && (
          <Button
            type="button"
            variant="primary"
            className="h-13 w-full"
            onClick={onContinue}
          >
            {t("authentication:continueButton")}
          </Button>
        )}

        {showResendButton && !resendSuccess && (
          <Button
            type="button"
            variant="primary"
            className="h-13 w-full"
            disabled={resending}
            onClick={() => {
              void handleResend();
            }}
          >
            {resending
              ? t("authentication:resendingActivationLinkButton")
              : t("authentication:resendActivationLinkButton")}
          </Button>
        )}

        <Button
          type="button"
          variant={showResendButton && !resendSuccess ? "secondary" : "primary"}
          className="h-13 w-full"
          onClick={handleBackToLogin}
        >
          {t("authentication:backToLogin")}
        </Button>
      </div>
    </LinkStatusFullBleed>
  );
}

export interface LinkStatusScreenProps {
  readonly initialStatus?: AnyLinkStatus | undefined;
  readonly mode?: ("activation" | "reset" | "generic") | undefined;
  readonly onResendActivationLink?: (() => Promise<void> | void) | undefined;
}

export function LinkStatusScreen({
  initialStatus,
  mode = "generic",
  onResendActivationLink,
}: LinkStatusScreenProps) {
  let resolvedStatus: AnyLinkStatus = initialStatus ?? "INVALID";

  try {
    const location = useLocation();
    const statusParam = new URLSearchParams(location.searchStr).get("status");
    if (
      statusParam === "VALID" ||
      statusParam === "EXPIRED" ||
      statusParam === "ALREADY_USED" ||
      statusParam === "SUPERSEDED" ||
      statusParam === "INVALID"
    ) {
      resolvedStatus = statusParam;
    }
  } catch {
    // If not in a router context, fallback to initialStatus or INVALID
  }

  return (
    <LinkStatusView
      status={resolvedStatus}
      mode={mode}
      onResendActivationLink={onResendActivationLink}
    />
  );
}
